import { useEffect, useCallback, useRef } from 'react';
import { useHeatmapStore } from '../stores/heatmapStore';
import { fetchHeatmapData } from '../services/heatmapService';

interface UseHeatmapDataResult {
  intensityMap: Map<string, number>;
  loading: boolean;
  error: string | null;
  isFromCache: boolean;
  refetch: () => Promise<void>;
}

export function useHeatmapData(
  userId: string | undefined,
  year?: number,
): UseHeatmapDataResult {
  const targetYear = year ?? new Date().getFullYear();

  // Subscribe to store for return values (re-renders when data changes)
  const store = useHeatmapStore();

  const intensityMap = userId
    ? store.getIntensityMap(userId, targetYear)
    : new Map<string, number>();
  const loading = userId
    ? store.isYearLoading(userId, targetYear)
    : false;
  const error = userId
    ? store.getYearError(userId, targetYear)
    : null;
  const fromCache = userId
    ? store.isYearCacheValid(userId, targetYear)
    : false;

  // Prevent concurrent fetches
  const fetchInProgressRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!userId) return;

    if (fetchInProgressRef.current) return;

    // Use getState() — doesn't create subscription, avoids dependency issues
    const { isYearCacheValid, setYearLoading, setYearData, setYearError } =
      useHeatmapStore.getState();

    if (isYearCacheValid(userId, targetYear)) {
      // console.log(`[useHeatmapData] Cache hit for ${userId}:${targetYear}`);
      return;
    }

    // console.log(`[useHeatmapData] Fetching ${userId}:${targetYear}`);
    fetchInProgressRef.current = true;
    setYearLoading(userId, targetYear, true);

    try {
      const { intensityMap: newMap } = await fetchHeatmapData(userId, targetYear);
      useHeatmapStore.getState().setYearData(userId, targetYear, newMap);
      // console.log(
      //   `[useHeatmapData] Cached ${userId}:${targetYear} (${newMap.size} entries)`
      // );
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to fetch heatmap data';
      console.error(
        `[useHeatmapData] Error for ${userId}:${targetYear}:`,
        msg
      );
      useHeatmapStore.getState().setYearError(userId, targetYear, msg);
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [userId, targetYear]); // ✅ Only stable primitive values

  const refetch = useCallback(async () => {
    if (!userId) return;
    useHeatmapStore.getState().clearYearCache(userId, targetYear);
    fetchInProgressRef.current = false; // Allow re-fetch
    await fetchData();
  }, [userId, targetYear, fetchData]);

  // Only runs when userId or year changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { intensityMap, loading, error, isFromCache: fromCache, refetch };
}